import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useRef,
  useMemo,
  useCallback,
} from 'react';
import { Amplify } from 'aws-amplify';
import { Hub } from 'aws-amplify/utils';
import { fetchAuthSession, signInWithRedirect } from 'aws-amplify/auth';
import { useLogger } from 'ui-components/src/hooks/useLogger';
import { ConfigContext } from '../ConfigContext';
import { sessionIdentity } from '../../authentication/sessionIdentity';
import {
  clearExpiredSession,
  configureSessionExpiry,
  getValidToken,
  hasExpiredSession,
  isAuthenticationError,
  expireSession,
  SESSION_EXPIRED_EVENT,
  SESSION_EXPIRED_MESSAGE,
  rememberExpiredSession,
  isSessionExpiring,
} from '../../util/sessionExpiry';

const AuthTokenContext = createContext<AuthTokenContextValue | null>(null);
export const useAuthTokenContext = () => {
  const context = useContext(AuthTokenContext);
  if (!context)
    throw new Error(
      'useAuthTokenContext can only be used inside AuthTokenProvider',
    );
  return context;
};

export const AuthContext: React.FC<React.PropsWithChildren<unknown>> = ({
  children,
}) => {
  const config = ConfigContext.useContainer();
  const logger = useLogger();
  const [token, setToken] = useState('');
  const tokenRef = useRef('');
  const [configured, setConfigured] = useState(false);
  const [sessionExpired, setSessionExpired] = useState(hasExpiredSession);
  const [authError, setAuthError] = useState(false);
  const [attempt, setAttempt] = useState(0);

  useEffect(() => {
    configureSessionExpiry(
      config?.cognito?.redirectSignOut || '/',
      config?.cognito,
    );
    const onExpired = () => {
      logger.info('[Auth] Session expired; redirecting to login.');
      tokenRef.current = '';
      setToken('');
      setSessionExpired(true);
    };
    window.addEventListener(SESSION_EXPIRED_EVENT, onExpired);
    // Amplify may return a tokenless session after a rejected refresh rather
    // than throwing. Capture the original failure before initialization ends.
    const stopListening = Hub.listen('auth', ({ payload }) => {
      if (
        payload.event === 'tokenRefresh_failure' &&
        isAuthenticationError(payload.data?.error)
      ) {
        void expireSession();
      }
    });
    return () => {
      stopListening();
      window.removeEventListener(SESSION_EXPIRED_EVENT, onExpired);
    };
  }, [config, logger]);

  useEffect(() => {
    if (!config?.cognito) return;
    Amplify.configure({
      Auth: {
        Cognito: {
          userPoolId: config.cognito.userPoolId,
          userPoolClientId: config.cognito.clientId,
          loginWith: {
            oauth: {
              domain: config.cognito.domain.replace(/^https?:\/\//, ''),
              scopes: config.cognito.scope,
              redirectSignIn: [config.cognito.redirectSignIn],
              redirectSignOut: [config.cognito.redirectSignOut],
              responseType: 'code',
            },
          },
        },
      },
    });
    setConfigured(true);
  }, [config]);

  useEffect(() => {
    if (!configured || sessionExpired) return;
    let disposed = false;
    const initialize = async () => {
      try {
        console.log('Starting auth initialization...');
        const session = await fetchAuthSession();
        const idToken = session.tokens?.idToken?.toString();
        console.log('Session fetched, has token:', !!idToken);
        if (disposed || hasExpiredSession()) return;
        if (idToken) {
          console.log('Setting token and rendering app');
          clearExpiredSession();
          tokenRef.current = idToken;
          setToken(idToken);
          setAuthError(false);
        } else {
          console.log('No token found, redirecting to login...');
          await signInWithRedirect();
        }
      } catch (error) {
        if (disposed) return;
        if (isAuthenticationError(error)) void expireSession();
        else {
          logger.warn(
            '[Auth] Session check failed; connection retry available.',
          );
          setAuthError(true);
        }
      }
    };
    void initialize();
    return () => {
      disposed = true;
    };
  }, [configured, sessionExpired, attempt, logger]);

  // Browser sleep can delay timers; also recheck on resume and reconnect.
  useEffect(() => {
    if (!configured || !token || sessionExpired) return;
    let timeout: number;
    let disposed = false;
    const schedule = (delay: number, force: boolean) => {
      clearTimeout(timeout);
      timeout = window.setTimeout(() => {
        void refreshToken(force);
      }, delay);
    };
    const refreshToken = async (force = false) => {
      try {
        if (force) logger.info('[Auth] Refreshing session.');
        const newToken = await getValidToken(force);
        if (disposed) return;
        if (force) logger.info('[Auth] Session refreshed.');
        tokenRef.current = newToken;
        setToken(newToken);
        const session = await fetchAuthSession();
        if (disposed) return;
        const exp = session.tokens?.idToken?.payload.exp;
        if (exp)
          schedule(Math.max(exp * 1000 - Date.now() - 10_000, 1000), true);
      } catch (error) {
        if (disposed) return;
        if (isAuthenticationError(error)) {
          void expireSession();
          return;
        }
        logger.warn(
          '[Auth] Session refresh unavailable; retrying in 30 seconds.',
        );
        schedule(30_000, false);
      }
    };
    const onResume = () => {
      if (document.visibilityState === 'visible') void refreshToken();
    };
    void refreshToken();
    window.addEventListener('online', onResume);
    document.addEventListener('visibilitychange', onResume);
    return () => {
      disposed = true;
      clearTimeout(timeout);
      window.removeEventListener('online', onResume);
      document.removeEventListener('visibilitychange', onResume);
    };
  }, [configured, token, sessionExpired, logger]);

  // Wait for logout to return, then continue to hosted login automatically.
  useEffect(() => {
    if (!configured || !sessionExpired || isSessionExpiring()) return;
    let disposed = false;
    let timeout: number;
    const redirect = async () => {
      setAuthError(false);
      try {
        logger.info('[Auth] Opening login after session expiry.');
        clearExpiredSession();
        await signInWithRedirect();
      } catch {
        if (disposed) return;
        rememberExpiredSession();
        logger.warn('[Auth] Login navigation failed; retrying automatically.');
        setAuthError(true);
        timeout = window.setTimeout(() => {
          void redirect();
        }, 5000);
      }
    };
    timeout = window.setTimeout(() => {
      void redirect();
    }, 1500);
    return () => {
      disposed = true;
      clearTimeout(timeout);
    };
  }, [configured, sessionExpired, logger]);

  const getToken = useCallback(() => tokenRef.current, []);
  const authSessionKey = sessionIdentity(token);
  const value = useMemo(
    () => ({ getToken, authSessionKey }),
    [getToken, authSessionKey],
  );
  if (sessionExpired) {
    return (
      <main
        style={{ minHeight: '100vh', display: 'grid', placeItems: 'center' }}
      >
        <section style={{ textAlign: 'center', padding: 24 }}>
          <h1>Redirecting to sign in</h1>
          <p role="status">{SESSION_EXPIRED_MESSAGE}</p>
          {authError && (
            <p role="alert">Unable to connect. Retrying automatically...</p>
          )}
        </section>
      </main>
    );
  }
  if (authError) {
    return (
      <main>
        <p role="alert">Unable to connect. Please try again.</p>
        <button
          type="button"
          onClick={() => {
            setAuthError(false);
            setAttempt((n) => n + 1);
          }}
        >
          Try again
        </button>
      </main>
    );
  }
  if (!token) {
    return (
      <div
        role="status"
        style={{
          minHeight: '100vh',
          width: '100%',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
        }}
      >
        Loading...
      </div>
    );
  }
  return (
    <AuthTokenContext.Provider value={value}>
      {children}
    </AuthTokenContext.Provider>
  );
};
export interface AuthTokenContextValue {
  getToken: () => string;
  authSessionKey?: string | null;
}
